import { useMemo, useState } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
  Cell,
  PieChart,
  Pie,
} from "recharts";
import {
  BarChart3,
  TrendingUp,
  Users,
  Award,
  Calendar,
  CheckCircle,
  Clock,
  AlertCircle,
  BookOpen,
  Eye,
  X,
  Filter as FilterIcon,
  RefreshCw,
  Printer,
  Share2,
  Target,
  Download,
  ChevronUp,
  ChevronDown,
  Minus,
} from "lucide-react";
import { useAuth } from "../../../app/AuthContext";
import { useSchoolProfile } from "../../../app/SchoolProfileContext";
import { useClasses } from "../../../hooks/useClasses";
import { useAnalytics } from "../../../hooks/useAnalytics";
import type {
  ClassData,
  AnalyticsReport,
  StudentRanking,
} from "../../../lib/api";

interface ReportsManagerProps {
  onClose?: () => void;
  onExport?: (report: any) => void;
  mode?: "modal" | "tab";
}

type TimeRange = "day" | "week" | "month" | "quarter" | "year";
type ReportType = "performance" | "engagement" | "attendance" | "completion";

export default function ReportsManager({
  onClose,
  onExport,
  mode = "modal",
}: ReportsManagerProps) {
  const { token } = useAuth();
  const { activeProfile } = useSchoolProfile();
  const [reportType, setReportType] = useState<ReportType>("performance");
  const [selectedClass, setSelectedClass] = useState<string>("all");
  const [timeRange, setTimeRange] = useState<TimeRange>("month");
  const [showFilters, setShowFilters] = useState(false);

  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");
  const [minScore, setMinScore] = useState<number | "">("");
  const [filterStatus, setFilterStatus] = useState<string>("Todos");

  const { data: apiClasses = [], isLoading: loadingClasses } = useClasses(
    token,
    activeProfile?.id,
  );

  const filters = useMemo(
    () => ({
      startDate: startDate || undefined,
      endDate: endDate || undefined,
      minScore: typeof minScore === "number" ? minScore : undefined,
      status: filterStatus !== "Todos" ? filterStatus : undefined,
    }),
    [startDate, endDate, minScore, filterStatus],
  );

  const {
    data: analytics,
    isLoading: loadingAnalytics,
    refetch: refetchAnalytics,
  } = useAnalytics(token, activeProfile?.id, selectedClass, timeRange, filters);

  const loading = loadingClasses || loadingAnalytics;

  const classes = useMemo(() => {
    const opts = [{ id: "all", name: "Todas as Turmas" }];
    apiClasses.forEach((c) =>
      opts.push({
        id: c.id,
        name: c.subject ? `${c.name} - ${c.subject}` : c.name,
      }),
    );
    return opts;
  }, [apiClasses]);

  const timeRanges = [
    { value: "day", label: "Hoje" },
    { value: "week", label: "Esta Semana" },
    { value: "month", label: "Este Mês" },
    { value: "quarter", label: "Este Trimestre" },
    { value: "year", label: "Este Ano" },
  ];

  const reportTypes = [
    {
      value: "performance",
      label: "Desempenho",
      icon: TrendingUp,
      color: "text-(--primary-color)",
    },
    {
      value: "engagement",
      label: "Engajamento",
      icon: Users,
      color: "text-(--success-color)",
    },
    {
      value: "attendance",
      label: "Presença",
      icon: Calendar,
      color: "text-(--purple-color)",
    },
    {
      value: "completion",
      label: "Conclusão",
      icon: CheckCircle,
      color: "text-(--warning-color)",
    },
  ];

  const handleExport = (format: string) => {
    if (!analytics) return;

    console.log(`Exportando relatório em ${format}`);
    const fileName = `Relatorio_${reportType}_${new Date().toISOString().split("T")[0]}`;

    if (format === "CSV" || format === "Excel") {
      // Conteúdo CSV
      let csvContent = "\uFEFF"; // BOM para Excel reconhecer UTF-8

      csvContent += "RELATÓRIO DE " + reportType.toUpperCase() + "\n";
      csvContent += `Gerado em: ${new Date().toLocaleString()}\n\n`;

      csvContent += "VISÃO GERAL\n";
      csvContent += `Métrica;Valor\n`;
      csvContent += `Nota Média;${analytics.overall.average_score.toFixed(2)}\n`;
      csvContent += `Taxa de Conclusão;${(analytics.overall.completion_rate * 100).toFixed(1)}%\n`;
      csvContent += `Total de Alunos;${analytics.overall.total_students}\n`;
      csvContent += `Total de Atividades;${analytics.overall.total_activities}\n\n`;

      csvContent += "DESEMPENHO POR DISCIPLINA\n";
      csvContent += "Disciplina;Nota Média\n";
      analytics.subjects.forEach((s) => {
        csvContent += `${s.subject};${s.score.toFixed(2)}\n`;
      });
      csvContent += "\n";

      csvContent += "TOP ALUNOS\n";
      csvContent += "Nome;Turma;Nota\n";
      analytics.rankings.top_students.forEach((s) => {
        csvContent += `${s.name};${s.class_name};${s.score.toFixed(2)}\n`;
      });
      csvContent += "\n";

      csvContent += "ALUNOS QUE PRECISAM DE ATENÇÃO\n";
      csvContent += "Nome;Turma;Nota;Participação\n";
      analytics.rankings.attention_needed.forEach((s) => {
        csvContent += `${s.name};${s.class_name};${s.score.toFixed(2)};${(s.participation * 100).toFixed(1)}%\n`;
      });
      csvContent += "\n";

      csvContent += "INSIGHTS DA IA\n";
      analytics.insights.forEach((insight) => {
        csvContent += `${insight}\n`;
      });

      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute("download", `${fileName}.csv`);
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      onExport?.(analytics);
    } else if (format === "PDF") {
      window.print();
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handleShare = () => {
    navigator.clipboard.writeText(window.location.href);
    alert("Link do relatório copiado para área de transferência!");
  };

  const handleRefresh = () => {
    refetchAnalytics();
  };

  const StatCard = ({ title, value, icon: Icon, trend, color }: any) => (
    <div className="p-4 rounded-xl border border-(--border-color) bg-(--card-bg) shadow-sm">
      <div className="flex items-center justify-between mb-3">
        <div className={`p-2 rounded-lg ${color} bg-opacity-10`}>
          <Icon className="w-5 h-5" />
        </div>
        {trend && (
          <span
            className={`text-xs font-medium px-2 py-1 rounded-full ${trend === "up" ? "bg-green-100 text-green-700" : trend === "down" ? "bg-red-100 text-red-700" : "bg-gray-100 text-gray-700"}`}
          >
            {trend === "up" ? "↑" : trend === "down" ? "↓" : "→"}
          </span>
        )}
      </div>
      <p className="text-sm text-(--text-tertiary) mb-1">{title}</p>
      <p className="text-2xl font-bold text-(--text-primary)">{value}</p>
    </div>
  );

  const renderPerformanceReport = () => {
    if (!analytics) return null;

    return (
      <div className="space-y-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard
            title="Nota Média"
            value={analytics.overall.average_score.toFixed(1)}
            icon={Award}
            trend={analytics.overall.performance_trend_direction}
            color="text-(--primary-color)"
          />
          <StatCard
            title="Taxa de Conclusão"
            value={`${Math.round(analytics.overall.completion_rate * 100)}%`}
            icon={CheckCircle}
            color="text-(--success-color)"
          />
          <StatCard
            title="Total de Alunos"
            value={analytics.overall.total_students}
            icon={Users}
            color="text-(--purple-color)"
          />
          <StatCard
            title="Atividades"
            value={analytics.overall.total_activities}
            icon={BookOpen}
            color="text-(--warning-color)"
          />
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          <div className="p-4 rounded-xl border border-(--border-color) bg-(--card-bg)">
            <h4 className="font-bold mb-4 text-(--text-primary)">
              Desempenho por Matéria
            </h4>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={analytics.subjects}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="subject" />
                  <YAxis domain={[0, 10]} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "var(--card-bg)",
                      borderColor: "var(--border-color)",
                      color: "var(--text-primary)",
                    }}
                  />
                  <Bar
                    dataKey="score"
                    fill="var(--primary-color)"
                    radius={[4, 4, 0, 0]}
                  >
                    {analytics.subjects.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={
                          index % 2 === 0
                            ? "var(--primary-color)"
                            : "var(--secondary-color)"
                        }
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="p-4 rounded-xl border border-(--border-color) bg-(--card-bg)">
            <h4 className="font-bold mb-4 text-(--text-primary)">
              Tendência de Desempenho
            </h4>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={analytics.performance_trends}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="label" />
                  <YAxis domain={[0, 10]} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "var(--card-bg)",
                      borderColor: "var(--border-color)",
                      color: "var(--text-primary)",
                    }}
                  />
                  <Line
                    type="monotone"
                    dataKey="score"
                    stroke="var(--primary-color)"
                    strokeWidth={3}
                    dot={{ r: 4 }}
                    activeDot={{ r: 6 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          <div className="p-4 rounded-xl border border-(--border-color) bg-(--card-bg)">
            <h4 className="font-bold mb-4 flex items-center gap-2 text-(--text-primary)">
              <Award className="w-5 h-5 text-yellow-500" />
              Top Alunos
            </h4>
            <div className="space-y-3">
              {analytics.rankings.top_students.map((student, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between p-2 rounded-lg bg-(--background-alt)"
                >
                  <div className="flex items-center gap-3">
                    <span className="font-bold text-yellow-600">
                      #{index + 1}
                    </span>
                    <div className="flex flex-col">
                      <span className="text-sm font-medium text-(--text-primary)">
                        {student.name}
                      </span>
                      <span className="text-[10px] text-(--text-tertiary)">
                        {student.class_name}
                      </span>
                    </div>
                  </div>
                  <span className="text-sm font-bold text-(--primary-color)">
                    {student.score.toFixed(1)}
                  </span>
                </div>
              ))}
              {analytics.rankings.top_students.length === 0 && (
                <p className="text-sm text-(--text-tertiary) text-center py-4">
                  Nenhum dado disponível
                </p>
              )}
            </div>
          </div>

          <div className="p-4 rounded-xl border border-(--border-color) bg-(--card-bg)">
            <h4 className="font-bold mb-4 flex items-center gap-2 text-(--text-primary)">
              <AlertCircle className="w-5 h-5 text-red-500" />
              Alunos que Precisam de Atenção
            </h4>
            <div className="space-y-3">
              {analytics.rankings.attention_needed.map((student, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between p-2 rounded-lg bg-red-50"
                >
                  <div className="flex flex-col">
                    <span className="text-sm font-medium text-red-900">
                      {student.name}
                    </span>
                    <span className="text-[10px] text-red-700">
                      {student.class_name}
                    </span>
                  </div>
                  <div className="flex flex-col items-end">
                    <span className="text-sm font-bold text-red-700">
                      {student.score.toFixed(1)}
                    </span>
                    <span className="text-[10px] text-red-500">
                      {Math.round(student.participation * 100)}% participação
                    </span>
                  </div>
                </div>
              ))}
              {analytics.rankings.attention_needed.length === 0 && (
                <p className="text-sm text-(--text-tertiary) text-center py-4">
                  Todos os alunos estão com bom desempenho!
                </p>
              )}
            </div>
          </div>

          <div className="p-4 rounded-xl border border-(--border-color) bg-(--card-bg)">
            <h4 className="font-bold mb-4 flex items-center gap-2 text-(--text-primary)">
              <Target className="w-5 h-5 text-purple-500" />
              Distribuição de Notas
            </h4>
            <div className="h-48">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={[
                      {
                        name: "Aprovados",
                        value: analytics.rankings.top_students.length,
                      },
                      {
                        name: "Atenção",
                        value: analytics.rankings.attention_needed.length,
                      },
                    ]}
                    cx="50%"
                    cy="50%"
                    innerRadius={40}
                    outerRadius={60}
                    paddingAngle={5}
                    dataKey="value"
                  >
                    <Cell fill="var(--success-color)" />
                    <Cell fill="var(--warning-color)" />
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="flex justify-center gap-4 mt-2">
              <div className="flex items-center gap-1 text-[10px]">
                <div className="w-2 h-2 rounded-full bg-(--success-color)" />
                <span className="text-(--text-secondary)">Aprovados</span>
              </div>
              <div className="flex items-center gap-1 text-[10px]">
                <div className="w-2 h-2 rounded-full bg-(--warning-color)" />
                <span className="text-(--text-secondary)">Atenção</span>
              </div>
            </div>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          <div className="p-4 rounded-xl border border-(--border-color) bg-(--card-bg)">
            <h4 className="font-bold mb-4 flex items-center gap-2 text-(--text-primary)">
              <TrendingUp className="w-5 h-5 text-blue-500" />
              Insights
            </h4>
            <ul className="space-y-3">
              {analytics.insights.map((insight, index) => (
                <li key={index} className="flex items-start gap-2">
                  <div className="w-2 h-2 rounded-full bg-blue-500 mt-2 shrink-0" />
                  <span className="text-sm text-(--text-secondary)">
                    {insight}
                  </span>
                </li>
              ))}
            </ul>
          </div>

          <div className="p-4 rounded-xl border border-(--border-color) bg-(--card-bg)">
            <h4 className="font-bold mb-4 flex items-center gap-2 text-(--text-primary)">
              <Target className="w-5 h-5 text-green-500" />
              Recomendações
            </h4>
            <ul className="space-y-3">
              {analytics.recommendations.map((rec, index) => (
                <li key={index} className="flex items-start gap-2">
                  <div className="w-2 h-2 rounded-full bg-green-500 mt-2 shrink-0" />
                  <span className="text-sm text-(--text-secondary)">{rec}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    );
  };

  const renderEngagementReport = () => {
    if (!analytics) return null;

    return (
      <div className="space-y-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard
            title="Participação"
            value={`${Math.round(analytics.overall.completion_rate * 100)}%`}
            icon={Users}
            trend={analytics.overall.performance_trend_direction}
            color="text-(--success-color)"
          />
          <StatCard
            title="Total Alunos"
            value={analytics.overall.total_students}
            icon={Users}
            color="text-(--primary-color)"
          />
          <StatCard
            title="Atividades"
            value={analytics.overall.total_activities}
            icon={BookOpen}
            color="text-(--purple-color)"
          />
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          <div className="p-4 rounded-xl border border-(--border-color) bg-(--card-bg)">
            <h4 className="font-bold mb-4 text-(--text-primary)">
              Top Engajados
            </h4>
            <div className="space-y-3">
              {analytics.rankings.top_students.map((student, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between p-3 rounded-lg bg-green-50 border border-green-100"
                >
                  <span className="font-medium text-green-900">
                    {student.name}
                  </span>
                  <span className="text-sm font-bold text-green-700">
                    {Math.round(student.participation * 100)}%
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div className="p-4 rounded-xl border border-(--border-color) bg-(--card-bg)">
            <h4 className="font-bold mb-4 text-(--text-primary)">
              Menos Engajados
            </h4>
            <div className="space-y-3">
              {analytics.rankings.attention_needed.map((student, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between p-3 rounded-lg bg-red-50 border border-red-100"
                >
                  <span className="font-medium text-red-900">
                    {student.name}
                  </span>
                  <span className="text-sm font-bold text-red-700">
                    {Math.round(student.participation * 100)}%
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderAttendanceReport = () => {
    if (!analytics) return null;

    return (
      <div className="space-y-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard
            title="Taxa de Presença"
            value={`${Math.round(analytics.overall.completion_rate * 100)}%`}
            icon={Calendar}
            trend={analytics.overall.performance_trend_direction}
            color="text-(--primary-color)"
          />
          <StatCard
            title="Total Alunos"
            value={analytics.overall.total_students}
            icon={Users}
            color="text-(--purple-color)"
          />
        </div>

        <div className="p-4 rounded-xl border border-(--border-color) bg-(--card-bg)">
          <h4 className="font-bold mb-4 text-(--text-primary)">
            Tendência de Engajamento
          </h4>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={analytics.performance_trends.map((t) => ({
                  ...t,
                  participation: t.participation * 100,
                }))}
              >
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="label" />
                <YAxis domain={[0, 100]} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: "var(--card-bg)",
                    borderColor: "var(--border-color)",
                    color: "var(--text-primary)",
                  }}
                />
                <Line
                  type="monotone"
                  dataKey="participation"
                  stroke="var(--success-color)"
                  strokeWidth={3}
                  dot={{ r: 4 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    );
  };

  const renderCompletionReport = () => {
    if (!analytics) return null;

    const completionData = [
      { name: "Concluídas", value: analytics.overall.completion_rate * 100 },
      {
        name: "Pendentes",
        value: (1 - analytics.overall.completion_rate) * 100,
      },
    ];

    const COLORS = ["var(--success-color)", "var(--border-color)"];

    return (
      <div className="space-y-6">
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <StatCard
            title="Taxa de Conclusão"
            value={`${Math.round(analytics.overall.completion_rate * 100)}%`}
            icon={CheckCircle}
            trend={analytics.overall.performance_trend_direction}
            color="text-(--success-color)"
          />
          <StatCard
            title="Total de Atividades"
            value={analytics.overall.total_activities}
            icon={BookOpen}
            color="text-(--primary-color)"
          />
          <StatCard
            title="Total de Alunos"
            value={analytics.overall.total_students}
            icon={Users}
            color="text-(--purple-color)"
          />
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          <div className="p-4 rounded-xl border border-(--border-color) bg-(--card-bg)">
            <h4 className="font-bold mb-4 text-(--text-primary)">
              Status de Conclusão Geral
            </h4>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={completionData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={80}
                    paddingAngle={5}
                    dataKey="value"
                  >
                    {completionData.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={COLORS[index % COLORS.length]}
                      />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="flex justify-center gap-6 mt-4">
              {completionData.map((entry, index) => (
                <div key={index} className="flex items-center gap-2">
                  <div
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: COLORS[index] }}
                  />
                  <span className="text-sm text-(--text-secondary)">
                    {entry.name}
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div className="p-4 rounded-xl border border-(--border-color) bg-(--card-bg)">
            <h4 className="font-bold mb-4 text-(--text-primary)">
              Insights de Conclusão
            </h4>
            <div className="space-y-4">
              {analytics.insights
                .filter(
                  (i) =>
                    i.toLowerCase().includes("conclusão") ||
                    i.toLowerCase().includes("participação"),
                )
                .map((insight, index) => (
                  <div
                    key={index}
                    className="flex items-start gap-3 p-3 rounded-lg bg-blue-50"
                  >
                    <AlertCircle className="w-5 h-5 text-blue-500 shrink-0 mt-0.5" />
                    <p className="text-sm text-blue-900">{insight}</p>
                  </div>
                ))}
              {analytics.insights.length === 0 && (
                <p className="text-sm text-(--text-tertiary) text-center py-8">
                  Nenhum insight disponível
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderReportContent = () => {
    if (loadingAnalytics) {
      return (
        <div className="flex flex-col items-center justify-center py-20 space-y-4">
          <RefreshCw className="w-10 h-10 text-(--primary-color) animate-spin" />
          <p className="text-(--text-secondary) font-medium">
            Analisando dados com IA...
          </p>
        </div>
      );
    }

    if (!analytics) {
      return (
        <div className="flex flex-col items-center justify-center py-20 space-y-4">
          <AlertCircle className="w-10 h-10 text-(--text-tertiary)" />
          <p className="text-(--text-secondary) font-medium">
            Nenhum dado disponível para o período selecionado.
          </p>
        </div>
      );
    }

    switch (reportType) {
      case "performance":
        return renderPerformanceReport();
      case "engagement":
        return renderEngagementReport();
      case "attendance":
        return renderAttendanceReport();
      case "completion":
        return renderCompletionReport();
      default:
        return renderPerformanceReport();
    }
  };

  return (
    <div
      className={
        mode === "modal"
          ? "fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50"
          : "w-full"
      }
    >
      <div
        className={`${
          mode === "modal"
            ? "bg-(--card-bg) rounded-2xl shadow-2xl w-full max-w-6xl max-h-[90vh] overflow-hidden"
            : "bg-(--card-bg) rounded-xl shadow-sm border border-(--border-color) overflow-hidden w-full"
        }`}
      >
        <div className="p-6 border-b border-(--border-color) flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-(--bg-accent)">
              <BarChart3 className="w-6 h-6 text-(--primary-color)" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-(--text-primary)">
                Relatórios
              </h2>
              <p className="text-sm text-(--text-tertiary)">
                Análise detalhada de desempenho
              </p>
            </div>
          </div>
          {mode === "modal" && (
            <button
              onClick={onClose}
              className="p-2 hover:bg-(--bg-accent) rounded-lg transition"
            >
              <X className="w-5 h-5 text-(--text-tertiary)" />
            </button>
          )}
        </div>

        <div className="p-4 border-b border-(--border-color) bg-(--bg-secondary)">
          <div className="flex flex-wrap gap-4 items-center justify-between">
            <div className="flex flex-wrap gap-3">
              <div className="flex items-center gap-2">
                <Eye className="w-4 h-4 text-(--text-tertiary)" />
                <select
                  value={reportType}
                  onChange={(e) => setReportType(e.target.value as ReportType)}
                  className="px-3 py-2 border border-(--border-color) rounded-lg bg-(--card-bg) text-(--text-primary) focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {reportTypes.map((type) => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-(--text-tertiary)" />
                <select
                  value={timeRange}
                  onChange={(e) => setTimeRange(e.target.value as TimeRange)}
                  className="px-3 py-2 border border-(--border-color) rounded-lg bg-(--card-bg) text-(--text-primary) focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {timeRanges.map((range) => (
                    <option key={range.value} value={range.value}>
                      {range.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex items-center gap-2">
                <BookOpen className="w-4 h-4 text-(--text-tertiary)" />
                <select
                  value={selectedClass}
                  onChange={(e) => setSelectedClass(e.target.value)}
                  className="px-3 py-2 border border-(--border-color) rounded-lg bg-(--card-bg) text-(--text-primary) focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {classes.map((cls) => (
                    <option key={cls.id} value={cls.id}>
                      {cls.name}
                    </option>
                  ))}
                </select>
              </div>

              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg transition ${showFilters ? "bg-(--bg-accent) text-blue-700" : "bg-(--card-bg) text-(--text-secondary) hover:bg-(--bg-accent)"}`}
              >
                <FilterIcon className="w-4 h-4" />
                Filtros
              </button>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={handleShare}
                className="p-2 hover:bg-(--bg-accent) rounded-lg transition text-(--text-tertiary)"
                title="Compartilhar"
              >
                <Share2 className="w-5 h-5" />
              </button>
              <div className="relative group">
                <button className="flex items-center gap-2 px-4 py-2 rounded-lg bg-(--primary-color) text-white hover:bg-blue-700 transition">
                  <Download className="w-4 h-4" />
                  Exportar
                </button>
                <div className="absolute top-full right-0 mt-1 w-48 bg-(--card-bg) border border-(--border-color) rounded-xl shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50 overflow-hidden">
                  <button
                    onClick={() => handleExport("PDF")}
                    className="w-full px-4 py-3 text-sm hover:bg-(--bg-accent) text-left text-(--text-primary) flex items-center gap-2 border-b border-(--border-color)"
                  >
                    <Download className="w-4 h-4 text-red-500" />
                    Exportar PDF
                  </button>
                  <button
                    onClick={() => handleExport("CSV")}
                    className="w-full px-4 py-3 text-sm hover:bg-(--bg-accent) text-left text-(--text-primary) flex items-center gap-2"
                  >
                    <Download className="w-4 h-4 text-green-600" />
                    Exportar Excel / CSV
                  </button>
                </div>
              </div>
            </div>
          </div>

          {showFilters && (
            <div className="mt-4 p-4 bg-(--card-bg) border border-(--border-color) rounded-lg">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1 text-(--text-primary)">
                    Data Inicial
                  </label>
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="w-full p-2 border border-(--border-color) rounded-lg text-(--text-primary) bg-(--card-bg)"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1 text-(--text-primary)">
                    Data Final
                  </label>
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="w-full p-2 border border-(--border-color) rounded-lg text-(--text-primary) bg-(--card-bg)"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1 text-(--text-primary)">
                    Nota Mínima
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="10"
                    step="0.1"
                    value={minScore}
                    onChange={(e) =>
                      setMinScore(
                        e.target.value === "" ? "" : Number(e.target.value),
                      )
                    }
                    className="w-full p-2 border border-(--border-color) rounded-lg text-(--text-primary) bg-(--card-bg)"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1 text-(--text-primary)">
                    Status
                  </label>
                  <select
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                    className="w-full p-2 border border-(--border-color) rounded-lg text-(--text-primary) bg-(--card-bg)"
                  >
                    <option value="Todos">Todos</option>
                    <option value="Aprovados">Aprovados (&gt;= 6.0)</option>
                    <option value="Em Recuperação">
                      Em Recuperação (4.0 - 5.9)
                    </option>
                    <option value="Reprovados">Reprovados (&lt; 4.0)</option>
                  </select>
                </div>
              </div>
              <div className="flex justify-end mt-4">
                <button
                  onClick={() => {
                    setStartDate("");
                    setEndDate("");
                    setMinScore("");
                    setFilterStatus("Todos");
                  }}
                  className="text-sm text-red-600 hover:text-red-700 font-medium"
                >
                  Limpar Filtros
                </button>
              </div>
            </div>
          )}
        </div>

        <div
          className={`overflow-y-auto p-6 ${mode === "modal" ? "max-h-[calc(90vh-200px)]" : ""}`}
        >
          {renderReportContent()}
        </div>

        <div className="p-4 border-t border-(--border-color) flex justify-between items-center">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-(--bg-accent)">
              <BarChart3 className="w-4 h-4 text-(--primary-color)" />
            </div>
            <span className="text-sm text-(--text-tertiary)">
              {reportType === "performance" &&
                "Relatório gerado com base em dados de desempenho dos alunos"}
              {reportType === "engagement" &&
                "Análise de engajamento nas atividades e participação"}
              {reportType === "attendance" &&
                "Relatório de frequência e presença dos alunos"}
            </span>
          </div>

          <div className="flex items-center gap-2 text-sm text-(--text-tertiary)">
            <Clock className="w-4 h-4" />
            Atualizado: {new Date().toLocaleDateString("pt-BR")}
          </div>
        </div>
      </div>
    </div>
  );
}
